// src/audio/sfx.ts
// Synthesized SFX via Web Audio API. Zero assets.
// AudioContext is created lazily on first user gesture (browser autoplay rules).

import { WEAPONS_BY_ID } from '../sim/weapons';

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = false;

export function isMuted(): boolean { return muted; }

export function setMuted(m: boolean): void {
  muted = m;
  if (masterGain) masterGain.gain.value = m ? 0 : 0.6;
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

/** Lazy-init audio context. Must be called from a user gesture handler. */
export function ensureAudio(): void {
  if (ctx) return;
  type WindowWithWebkit = Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };
  const w = window as WindowWithWebkit;
  const AudioCtor: typeof AudioContext | undefined = window.AudioContext || w.webkitAudioContext;
  if (!AudioCtor) return;
  ctx = new AudioCtor();
  masterGain = ctx.createGain();
  masterGain.gain.value = muted ? 0 : 0.6;
  masterGain.connect(ctx.destination);
}

function now(): number { return ctx!.currentTime; }

// --- Primitive synthesis helpers -----------------------------------------

interface ToneOpts {
  type?: OscillatorType;
  freqStart: number;
  freqEnd?: number;     // sweep target
  duration: number;     // seconds
  attack?: number;      // gain attack
  release?: number;     // gain release
  peakGain?: number;    // 0..1
  detune?: number;      // cents
}

function tone(o: ToneOpts): void {
  if (!ctx || !masterGain || muted) return;
  const t0 = now();
  const dur = o.duration;
  const osc = ctx.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freqStart, t0);
  if (o.freqEnd !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(0.001, o.freqEnd), t0 + dur);
  }
  if (o.detune) osc.detune.setValueAtTime(o.detune, t0);
  const g = ctx.createGain();
  const peak = o.peakGain ?? 0.4;
  const att = o.attack ?? 0.005;
  const rel = o.release ?? Math.max(0.05, dur * 0.6);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + att);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + rel);
  osc.connect(g).connect(masterGain);
  osc.start(t0);
  osc.stop(t0 + dur + rel + 0.05);
}

interface NoiseOpts {
  duration: number;
  peakGain?: number;
  filterFreq?: number;     // lowpass cutoff start
  filterEndFreq?: number;  // sweep
  filterQ?: number;
}

function noiseBurst(o: NoiseOpts): void {
  if (!ctx || !masterGain || muted) return;
  const t0 = now();
  const dur = o.duration;
  const sampleRate = ctx.sampleRate;
  const buf = ctx.createBuffer(1, Math.max(1, Math.floor(sampleRate * dur)), sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = o.filterQ ?? 1;
  filter.frequency.setValueAtTime(o.filterFreq ?? 1200, t0);
  if (o.filterEndFreq !== undefined) {
    filter.frequency.exponentialRampToValueAtTime(Math.max(50, o.filterEndFreq), t0 + dur);
  }
  const g = ctx.createGain();
  const peak = o.peakGain ?? 0.5;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(masterGain);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
}

// --- Public SFX -----------------------------------------------------------

/** Weapon launch — scales with weapon power. */
export function sfxLaunch(weaponId: string): void {
  ensureAudio();
  if (!ctx || muted) return;
  const w = WEAPONS_BY_ID[weaponId];
  if (!w) return;
  const threat = Math.min(1, (w.maxDamage + w.blastRadius * 0.8) / 330);

  // Tracers / tiny things: short blip
  if (threat < 0.12) {
    tone({ type: 'square', freqStart: 1400, freqEnd: 600, duration: 0.05, peakGain: 0.12 });
    return;
  }

  // Whoosh: descending noise + low tone, longer/lower for bigger weapons
  const dur = 0.18 + threat * 0.35;
  const startFreq = 1800 - threat * 800;   // bigger -> deeper
  const endFreq = 200 + (1 - threat) * 200;
  noiseBurst({
    duration: dur,
    peakGain: 0.18 + threat * 0.25,
    filterFreq: startFreq,
    filterEndFreq: endFreq,
    filterQ: 1.5,
  });
  // Sub-bass thump for nuke-tier
  if (threat > 0.55) {
    tone({
      type: 'sine',
      freqStart: 90 - threat * 30,
      freqEnd: 35,
      duration: dur * 0.8,
      peakGain: 0.45,
      attack: 0.005,
      release: 0.2,
    });
  }
}

/** Explosion impact. radius ~6..200 from blastRadius. */
export function sfxExplosion(radius: number, weaponId?: string): void {
  ensureAudio();
  if (!ctx || muted) return;
  const t = Math.min(1, radius / 200);
  const w = weaponId ? WEAPONS_BY_ID[weaponId] : undefined;
  const isNuke = w && (weaponId === 'nuke' || weaponId === 'deaths-head' || weaponId?.includes('nuke'));
  const isEnergy = weaponId === 'laser' || weaponId === 'plasma-blast';
  const isDirt = weaponId?.includes('dirt');
  const isRiot = weaponId?.includes('riot');

  if (radius < 8) {
    // tiny pop (laser)
    tone({ type: 'square', freqStart: 900, freqEnd: 200, duration: 0.08, peakGain: 0.18 });
    return;
  }

  if (isEnergy) {
    // Crackly zap
    tone({ type: 'sawtooth', freqStart: 1600, freqEnd: 200, duration: 0.18, peakGain: 0.25 });
    noiseBurst({ duration: 0.15, peakGain: 0.18, filterFreq: 4000, filterEndFreq: 800, filterQ: 6 });
    return;
  }

  if (isDirt || isRiot) {
    // Earthy thud, less crackle
    noiseBurst({
      duration: 0.18 + t * 0.2,
      peakGain: 0.3 + t * 0.25,
      filterFreq: 600,
      filterEndFreq: 80,
      filterQ: 0.9,
    });
    tone({ type: 'sine', freqStart: 80, freqEnd: 30, duration: 0.2 + t * 0.3, peakGain: 0.45 });
    return;
  }

  // Generic / nuke explosion: noisy crackle + sub boom
  const dur = 0.3 + t * 0.6;
  noiseBurst({
    duration: dur,
    peakGain: 0.45 + t * 0.3,
    filterFreq: 2200,
    filterEndFreq: 120,
    filterQ: 1.2,
  });
  tone({
    type: 'sine',
    freqStart: 120 - t * 50,
    freqEnd: 25,
    duration: dur * 0.9,
    peakGain: 0.6,
    release: 0.4,
  });
  if (isNuke) {
    // Extra sub-rumble
    tone({
      type: 'triangle',
      freqStart: 60,
      freqEnd: 18,
      duration: 1.2,
      peakGain: 0.5,
      release: 0.6,
    });
  }
}

/** Tank destroyed (separate from environmental explosion). */
export function sfxTankDestroyed(): void {
  ensureAudio();
  if (!ctx || muted) return;
  noiseBurst({ duration: 0.5, peakGain: 0.4, filterFreq: 1500, filterEndFreq: 80, filterQ: 1.5 });
  tone({ type: 'sawtooth', freqStart: 200, freqEnd: 50, duration: 0.5, peakGain: 0.3 });
}

/** UI click / button tap. */
export function sfxClick(): void {
  ensureAudio();
  if (!ctx || muted) return;
  tone({ type: 'square', freqStart: 800, freqEnd: 600, duration: 0.04, peakGain: 0.12, release: 0.04 });
}

/** Turn change chime. */
export function sfxTurnChange(): void {
  ensureAudio();
  if (!ctx || muted) return;
  tone({ type: 'sine', freqStart: 660, duration: 0.08, peakGain: 0.15, release: 0.1 });
  setTimeout(() => tone({ type: 'sine', freqStart: 880, duration: 0.1, peakGain: 0.18, release: 0.12 }), 80);
}

/** Match win fanfare. */
export function sfxMatchWin(): void {
  ensureAudio();
  if (!ctx || muted) return;
  const notes = [523, 659, 784, 1047]; // C E G C
  notes.forEach((f, i) => {
    setTimeout(() => tone({ type: 'triangle', freqStart: f, duration: 0.18, peakGain: 0.28, release: 0.15 }), i * 130);
  });
}
